import {
    Button,
    Checkbox,
    Container,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorDetailContainer } from "../../api/config";
import { AccountService, ClientClaimDto, UserDto } from "../../api/identity";
import { PageHeader, PrivilegeAlert, Spinner } from "../../components/exports";
import ErrorParser from "../../components/shared/ErrorParser";
import { UserAccessTypes } from "../../components/shared/PrivilegeAlert";
import { fixMobileNumber } from "../../utils/language";

export default function UpdateUser() {
    let { id } = useParams();

    const navigate = useNavigate();

    const [user, setUser] = useState<UserDto>();
    const [availableClaims, setAvailableClaims] = useState<ClientClaimDto[]>(
        []
    );
    const [selectedClaims, setSelectedClaims] = useState<number[]>([]);
    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [requestInProgress, setRequestInProgress] = useState(false);

    useEffect(() => {
        const userId = parseInt(id);

        AccountService.getUserById(userId).then((r) => {
            setUser(r);
            setSelectedClaims(r.claims.map((c) => c.id));
        });

        AccountService.listAvailableClaims().then((r) => {
            setAvailableClaims(r);
        });
    }, []);

    const handleDisplayNameChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const userToUpdate = { ...user, displayName: elem.target.value };
        setUser(userToUpdate);
    };

    const handlePhoneNumberChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const userToUpdate = {
            ...user,
            mobileNumber: fixMobileNumber(elem.target.value),
        };
        setUser(userToUpdate);
    };

    const toggleClaim = (id: number) => {
        if (selectedClaims.includes(id)) {
            setSelectedClaims(selectedClaims.filter((sc) => sc !== id));
        } else {
            setSelectedClaims([...selectedClaims, id]);
        }
    };

    const updateUser = () => {
        setRequestInProgress(true);

        setError(null);

        AccountService.updateUser(parseInt(id), {
            displayName: user.displayName,
            mobileNumber: user.mobileNumber,
            claimIds: selectedClaims,
        })
            .then((_) => {
                navigate("/users");
            })
            .catch((e) => {
                setError(e);
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    if (!user) {
        return <Spinner />;
    }

    return (
        <Container maxWidth="xs">
            <PageHeader title="ویرایش کاربر" />

            <PrivilegeAlert access={UserAccessTypes.ManageUsers} />

            <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        onChange={handleDisplayNameChange}
                        value={user.displayName}
                        label="نام نمایشی"
                        variant="outlined"
                    />

                    <TextField
                        fullWidth
                        onChange={handlePhoneNumberChange}
                        value={user.mobileNumber}
                        label="شماره موبایل"
                        variant="outlined"
                    />

                    {availableClaims.map((c) => {
                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                key={c.id}
                            >
                                <Checkbox
                                    onChange={() => toggleClaim(c.id)}
                                    checked={selectedClaims.includes(c.id)}
                                />
                                <Typography>{c.displayName}</Typography>
                            </Stack>
                        );
                    })}

                    <ErrorParser error={error} />

                    <Button
                        fullWidth
                        disabled={requestInProgress}
                        variant="contained"
                        onClick={updateUser}
                    >
                        ویرایش کاربر
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
}
